import { MathJax, MathJaxContext } from "better-react-mathjax";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import imagesService from "../../services/imagesService";
import { FormulaCase, IImage } from "../../types/image";
import { Status } from "../../types/status";
import { UserCheckStatus } from "../../types/userCheck";
import {
  ImageActions,
  ImageBlock,
  ImageButton,
  ImageCard,
  ImageComparison,
  ImageControls,
  ImageDisplay,
  ImageErrorMessage,
  ImageFormGroup,
  ImageInstructions,
  ImageInstructionsText,
  ImageInstructionsTitle,
  ImageLatexError,
  ImagePreview,
  ImageRadio,
  ImageRadioGroup,
  ImageRadioLabel,
  ImageRadioText,
  ImageSection,
  ImageTextArea,
  ImageTitle,
  ImageWrapper,
} from "./ImageStyles";

// Constants
const IMAGE_FORMULA_CASES: { value: FormulaCase; label: string }[] = [
  { value: FormulaCase.Uppercase, label: "Верхний регистр" },
  { value: FormulaCase.Lowercase, label: "Нижний регистр" },
  { value: FormulaCase.Normal, label: "Обычный регистр" },
];

const IMAGE_INSTRUCTIONS = [
  "Шаг 1. Сравните оригинальное изображение слева с рендером LaTeX формулы справа. Если есть различия, то поправьте " +
  "текст формулы. Помните, что удалять символ $ по бокам формулы не нужно",
  "Шаг 2. Проверьте, что выставленный регистр у радиокнопок соответствует оригиналу",
  "После этого можете нажать на кнопку 'Всё корректно' или 'Сохранить изменения', если вы что-то поправили",
  "Если вам попалась таблица, график, чертёж или что-то не относящееся к формулам, то укажите причину в комментарии и " +
  "нажмите 'Пропустить'",
];

// Types
interface ImageState {
  currentImage: IImage | null;
  isLoading: boolean;
  latexFormula: string;
  formulaCase: FormulaCase;
  comment: string;
  renderError: boolean;
  error: string;
}

// Custom Hooks
const useImageState = () => {
  const [state, setState] = useState<ImageState>({
    currentImage: null,
    isLoading: true,
    latexFormula: "",
    formulaCase: FormulaCase.Normal,
    comment: "",
    renderError: false,
    error: "",
  });

  const updateState = (updates: Partial<ImageState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  return { state, updateState };
};

const Image: React.FC = () => {
  const navigate = useNavigate();
  const { state, updateState } = useImageState();

  const hasChanges = state.currentImage
    ? state.latexFormula !== state.currentImage.formula || state.formulaCase !== state.currentImage.formulaCase
    : false;

  const fetchNewImage = async () => {
    try {
      updateState({ isLoading: true, error: "" });

      const response = await imagesService.getImage();

      if (response.status === Status.Success) {
        if (!response.data) {
          navigate("/");
          return;
        }

        updateState({
          currentImage: response.data,
          latexFormula: response.data.formula,
          formulaCase: response.data.formulaCase,
          renderError: false,
          comment: "",
        });

        return;
      }

      if (response.status === Status.AuthError) {
        navigate("/auth");
      }
    } catch (error) {
      updateState({ error: "Ошибка при загрузке изображения" });
      console.error("Error fetching image:", error);
    } finally {
      updateState({ isLoading: false });
    }
  };

  const saveImageCheck = async (status: UserCheckStatus, skippedComment: string | null = null) => {
    if (!state.currentImage) return;

    await imagesService.saveCheck({
      imageHash: state.currentImage.hash,
      status,
      formula: state.latexFormula,
      formulaCase: state.formulaCase,
      skippedComment,
    });
  };

  const handleSubmitCorrect = async () => {
    if (!state.currentImage) return;

    const status = hasChanges ? UserCheckStatus.Incorrect : UserCheckStatus.Correct;
    await saveImageCheck(status);
    await fetchNewImage();
  };

  const handleSkip = async () => {
    if (!state.currentImage || !state.comment.trim()) return;

    await saveImageCheck(UserCheckStatus.Skipped, state.comment);
    await fetchNewImage();
  };

  const handleFinish = () => {
    imagesService.deleteInReview();
    navigate("/");
  };

  const handleInputChange =
    (field: keyof Pick<ImageState, "latexFormula" | "comment">) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      updateState({ [field]: event.target.value });
    };

  const handleFormulaCaseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateState({ formulaCase: event.target.value as FormulaCase });
  };

  const handleImageError = () => {
    updateState({ error: "Ошибка загрузки изображения" });
  };

  useEffect(() => {
    fetchNewImage();
  }, []);

  useEffect(() => {
    window.addEventListener("beforeunload", handleFinish);

    return () => {
      window.removeEventListener("beforeunload", handleFinish);
    };
  }, []);

  const renderLoadingState = () => (
    <ImageBlock>
      <ImageCard>
        <ImageTitle>Загрузка...</ImageTitle>
      </ImageCard>
    </ImageBlock>
  );

  const renderImageComparison = () => (
    <ImageComparison>
      <ImageSection>
        <ImageDisplay>
          <ImageWrapper>
            <ImagePreview
              src={state.currentImage!.path}
              alt="Оригинальное изображение формулы"
              onError={handleImageError}
            />
          </ImageWrapper>
        </ImageDisplay>
      </ImageSection>

      <ImageSection>
        <ImageDisplay>
          <ImageWrapper>
            <MathJaxContext>
              {state.renderError ? (
                <ImageLatexError>Невозможно отобразить LaTeX формулу</ImageLatexError>
              ) : (
                <MathJax dynamic>{"\\(" + state.latexFormula + "\\)"}</MathJax>
              )}
            </MathJaxContext>
          </ImageWrapper>
        </ImageDisplay>
      </ImageSection>
    </ImageComparison>
  );

  const renderFormulaCaseOptions = () => (
    <ImageRadioGroup>
      {IMAGE_FORMULA_CASES.map(({ value, label }) => (
        <ImageRadioLabel key={value}>
          <input type="radio" value={value} checked={state.formulaCase === value} onChange={handleFormulaCaseChange} />
          <ImageRadio $checked={state.formulaCase === value} />
          <ImageRadioText $checked={state.formulaCase === value}>{label}</ImageRadioText>
        </ImageRadioLabel>
      ))}
    </ImageRadioGroup>
  );

  const renderInstructions = () => (
    <ImageInstructions>
      <ImageInstructionsTitle>Инструкция по проверке</ImageInstructionsTitle>
      {IMAGE_INSTRUCTIONS.map((instruction, index) => (
        <ImageInstructionsText key={index}>• {instruction}</ImageInstructionsText>
      ))}
    </ImageInstructions>
  );

  const renderControls = () => (
    <ImageControls>
      <ImageFormGroup>
        <label htmlFor="imageLatexFormula">LaTeX формула:</label>
        <ImageTextArea
          id="imageLatexFormula"
          value={state.latexFormula}
          onChange={handleInputChange("latexFormula")}
          placeholder="Введите LaTeX формулу..."
        />
      </ImageFormGroup>

      <ImageFormGroup>
        <label>Регистр формулы:</label>
        {renderFormulaCaseOptions()}
      </ImageFormGroup>

      <ImageFormGroup>
        <label htmlFor="imageComment">Комментарий (для пропуска):</label>
        <ImageTextArea
          id="imageComment"
          value={state.comment}
          onChange={handleInputChange("comment")}
          placeholder="Укажите причину пропуска..."
        />
      </ImageFormGroup>

      <ImageActions>
        <ImageButton onClick={handleSubmitCorrect} $disabled={state.isLoading} $variant="primary">
          {hasChanges ? "Сохранить изменения" : "Всё корректно"}
        </ImageButton>

        <ImageButton onClick={handleSkip} $disabled={!state.comment.trim() || state.isLoading}>
          Пропустить
        </ImageButton>

        <ImageButton onClick={handleFinish} $disabled={state.isLoading}>
          Закончить проверку
        </ImageButton>
      </ImageActions>
    </ImageControls>
  );

  if (state.isLoading) {
    return renderLoadingState();
  }

  return (
    <ImageBlock>
      <ImageCard>
        <ImageTitle>Проверка LaTeX формул</ImageTitle>

        {state.error && <ImageErrorMessage>{state.error}</ImageErrorMessage>}

        {renderImageComparison()}
        {renderControls()}
        {renderInstructions()}
      </ImageCard>
    </ImageBlock>
  );
};

export { Image };
